import { useState } from "react";
import { Link } from "react-router-dom";
import { investmentProducts } from "../../../mocks/investments";
import CompareProductsTable from "./CompareProductsTable";

const categories = ["All", "UK Structured Product", "NASDAQ Dubai", "DFM Listed"];
const sortOptions = [
  { label: "Default", value: "default" },
  { label: "Return: High → Low", value: "return_desc" },
  { label: "Min. Investment: Low → High", value: "min_asc" },
  { label: "Min. Investment: High → Low", value: "min_desc" },
];

function parseMin(str: string) {
  return parseInt(str.replace(/[^0-9]/g, ""), 10) || 0;
}
function parseReturn(str: string) {
  return parseFloat(str.replace(/[^0-9.]/g, "")) || 0;
}

export default function InvestmentProducts() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortValue, setSortValue] = useState("default");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  const filtered = activeCategory === "All"
    ? investmentProducts
    : investmentProducts.filter((p) => p.category === activeCategory);

  const sorted = [...filtered].sort((a, b) => {
    if (sortValue === "min_asc") return parseMin(a.minInvestment) - parseMin(b.minInvestment);
    if (sortValue === "min_desc") return parseMin(b.minInvestment) - parseMin(a.minInvestment);
    if (sortValue === "return_desc") return parseReturn(b.targetReturn) - parseReturn(a.targetReturn);
    return 0;
  });

  const activeSortLabel = sortOptions.find((s) => s.value === sortValue)?.label ?? "Sort";

  return (
    <section className="bg-[#071A2E] py-20 md:py-28 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#C9A84C]/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#C9A84C]/3 translate-y-1/2 -translate-x-1/2 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
              Multi-Asset Portfolio
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Our Investment Products
            </h2>
            <p className="text-slate-400 text-sm mt-2 max-w-lg">
              From UK social housing structured products to NASDAQ Dubai sovereign bonds — a curated range of income-generating opportunities for qualified investors.
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#C9A84C]/40 text-[#C9A84C] text-sm font-semibold hover:bg-[#C9A84C]/10 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-customer-service-line leading-none" />
            Speak to an Adviser
          </Link>
        </div>

        {/* Filters + Sort row */}
        <div className="flex flex-col gap-3 mb-10">
          {/* Category pills */}
          <div className="flex items-center gap-2 flex-wrap px-1 py-1 bg-white/5 rounded-full w-fit">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-[#C9A84C] text-[#071A2E]"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Sort + Compare row */}
          <div className="flex flex-wrap items-center gap-3">
          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu((v) => !v)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-slate-300 text-xs font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-sort-desc leading-none text-[#C9A84C]" />
              {activeSortLabel}
              <i className={`ri-arrow-down-s-line leading-none transition-transform ${showSortMenu ? "rotate-180" : ""}`} />
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-[#0D2B4A] border border-white/10 rounded-xl overflow-hidden z-30">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortValue(opt.value); setShowSortMenu(false); }}
                    className={`w-full text-left px-4 py-3 text-xs transition-colors cursor-pointer flex items-center justify-between ${
                      sortValue === opt.value ? "text-[#C9A84C] bg-[#C9A84C]/10" : "text-slate-300 hover:bg-white/5"
                    }`}
                  >
                    {opt.label}
                    {sortValue === opt.value && <i className="ri-check-line text-[#C9A84C] leading-none" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Compare toggle */}
          <button
            onClick={() => setShowCompare((v) => !v)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
              showCompare
                ? "bg-[#C9A84C] border-[#C9A84C] text-[#071A2E]"
                : "border-[#C9A84C]/40 text-[#C9A84C] hover:bg-[#C9A84C]/10"
            }`}
          >
            <i className={`${showCompare ? "ri-close-line" : "ri-table-2"} leading-none`} />
            {showCompare ? "Hide Comparison" : "Compare UK Products"}
          </button>
          </div>
        </div>

        {/* ── FLAGSHIP PRODUCT CALLOUT ── */}
        <div
          className="mb-8 rounded-2xl overflow-hidden border border-[#C9A84C]/30"
          style={{ background: "linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(201,168,76,0.04) 60%, rgba(7,26,46,0.5) 100%)" }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 px-6 md:px-10 py-7">
            <div className="flex items-center gap-5">
              <div
                className="w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0 border border-[#C9A84C]/40"
                style={{ background: "rgba(201,168,76,0.15)" }}
              >
                <i className="ri-award-fill text-[#C9A84C] text-2xl leading-none" />
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-[#C9A84C] bg-[#C9A84C]/15 border border-[#C9A84C]/30 px-2.5 py-1 rounded-full mb-2">
                  <i className="ri-star-fill text-xs leading-none" />
                  Flagship Product — Currently Open
                </span>
                <h3
                  className="text-white font-bold text-lg leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Social Housing Structured Product
                </h3>
                <p className="text-slate-400 text-xs mt-1">5-Year term · UK Assisted Living Asset-Backed · First Legal Charge Security · Monthly Distributions</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 flex-shrink-0">
              <div className="text-center">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Target Return</p>
                <p
                  className="text-3xl font-bold text-[#C9A84C]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  10.00%
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Per Annum · Monthly Income</p>
              </div>
              <Link
                to="/social-housing"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm whitespace-nowrap cursor-pointer transition-all"
                style={{ background: "linear-gradient(135deg, #C9A84C, #E2C27A)", color: "#071A2E" }}
              >
                View Full Product Details
                <i className="ri-arrow-right-line leading-none" />
              </Link>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sorted.map((p) => (
            <article
              key={p.id}
              className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:bg-white/8 hover:border-[#C9A84C]/30 transition-all duration-300 group flex flex-col"
            >
              <div className="p-6 flex-1">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${p.badgeColor}20` }}
                  >
                    <i className={`${p.icon} text-xl leading-none`} style={{ color: p.badgeColor }} />
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span
                      className="text-xs font-bold px-3 py-1 rounded-full"
                      style={{ backgroundColor: `${p.badgeColor}25`, color: p.badgeColor }}
                    >
                      {p.badge}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        p.status === "Open"
                          ? "bg-green-500/15 text-green-400"
                          : "bg-amber-500/15 text-amber-400"
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>
                </div>

                <p className="text-slate-500 text-xs font-medium mb-1">{p.category}</p>
                <h3
                  className="font-bold text-white text-base leading-snug mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {p.name}
                </h3>
                <p className="text-slate-400 text-xs leading-relaxed mb-5">{p.description}</p>

                <div className="grid grid-cols-2 gap-2 mb-5">
                  {[
                    { label: "Target Return", value: p.targetReturn, highlight: true },
                    { label: "Frequency", value: p.frequency },
                    { label: "Term", value: p.term },
                    { label: "Currency", value: p.currency },
                    { label: "Min. Investment", value: p.minInvestment },
                    { label: "Listing", value: p.listing },
                  ].map((m) => (
                    <div key={m.label} className="bg-white/5 rounded-lg px-3 py-2">
                      <p className="text-slate-500 text-xs mb-0.5">{m.label}</p>
                      <p className={`text-xs font-bold ${m.highlight ? "text-[#C9A84C]" : "text-white"}`}>
                        {m.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-verified-badge-line text-[#C9A84C] text-xs leading-none" />
                  </div>
                  <span className="text-slate-400 text-xs">Security / Rating: <strong className="text-slate-300">{p.rating}</strong></span>
                </div>

                <div className="flex flex-col gap-1.5">
                  {p.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-2">
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-green-400 text-xs leading-none" />
                      </div>
                      <span className="text-slate-400 text-xs">{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-white/10 p-4">
                <Link
                  to={p.link}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: `${p.badgeColor}20`, color: p.badgeColor }}
                >
                  {p.id === 1 ? "View Full Product Details" : "View Product Details"}
                  <i className="ri-arrow-right-line leading-none" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Compare Table */}
        {showCompare && <CompareProductsTable />}

        {/* Disclaimer */}
        <p className="text-slate-600 text-xs mt-8 text-center max-w-3xl mx-auto leading-relaxed">
          All investment products involve risk. Capital may be at risk. Information provided is for illustrative and informational purposes only and does not constitute financial advice. Returns are targets and not guaranteed. Please seek independent financial advice before investing.
        </p>
      </div>
    </section>
  );
}
